import React from 'react'
import PropTypes from 'prop-types'

import {
  Form,
  Label,
  Input,
  Button,
  ErrorText,
  TextArea,
  Dropdown,
} from 'components/Forms'
import Loader from 'components/Loader'
import HeaderOne from 'components/Headers/HeaderOne'
import Text from 'components/Text'
import { AddressType } from 'types'

import BackLink from 'UserApp/components/BackLink'

import style from './style.module.scss'
import countries from 'lib/countries'
import { StateLabel } from '@pet-notify/ui'
import canadianProvinces from 'lib/canadian-provinces'
import usStates from 'lib/us-states'

AddressForm.propTypes = {
  isConfirmingRemove: PropTypes.bool,
  isEditing: PropTypes.bool,
  isLoading: PropTypes.bool,
  isProcessing: PropTypes.bool,
  onCancelRemove: PropTypes.func,
  onConfirmRemove: PropTypes.func,
  onGoBack: PropTypes.func,
  onRemove: PropTypes.func,
  onSubmit: PropTypes.func,
  title: PropTypes.string,
  values: PropTypes.shape(AddressType),
  customError: PropTypes.string,
}

function AddressForm(props) {
  const {
    isConfirmingRemove,
    isEditing,
    isLoading,
    isProcessing,
    onCancelRemove,
    onConfirmRemove,
    onGoBack,
    onRemove,
    onSubmit,
    title,
    values,
    customError,
  } = props

  if (isLoading) {
    return <Loader />
  }

  const { email, isPending, isDeclined } = values

  return (
    <div className={style.wrapper}>
      {onGoBack && (
        <BackLink onClick={onGoBack} className={style.backLink}>
          Back
        </BackLink>
      )}
      <HeaderOne className={style.header}>
        {title ? `${title}` : isEditing ? `Edit address` : `Add address`}
      </HeaderOne>
      <Form onSubmit={onSubmit} values={values}>
        {({ onChange, values, error }) =>
          isProcessing ? (
            <Loader />
          ) : (
            <React.Fragment>
              <Label htmlFor='address'>Address</Label>
              <Input
                name='address'
                id='address'
                value={values.address}
                onChange={onChange}
              />
              <Label htmlFor='apartmentSuite'>Apartment / Suite</Label>
              <Input
                type='text'
                name='apartmentSuite'
                id='apartmentSuite'
                value={values.apartmentSuite}
                onChange={onChange}
              />
              <Label htmlFor='city'>City</Label>
              <Input
                type='text'
                name='city'
                id='city'
                value={values.city}
                onChange={onChange}
              />
              <Label htmlFor='country'>Country</Label>
              <Dropdown
                name='country'
                options={countries}
                id='country'
                value={values.country}
                onChange={onChange}
              />
              <StateLabel htmlFor='state' country={values.country} />
              {values.country === 'US' || values.country === 'CA' ? (
                <Dropdown
                  includeBlank
                  name='state'
                  options={
                    values.country === 'US' ? usStates : canadianProvinces
                  }
                  id='state'
                  value={values.state}
                  onChange={onChange}
                />
              ) : (
                <Input
                  type='text'
                  name='state'
                  id='state'
                  value={values.state}
                  onChange={onChange}
                />
              )}
              <Label htmlFor='zipCode'>Zip Code</Label>
              <Input
                type='text'
                name='zipCode'
                id='zipCode'
                value={values.zipCode}
                onChange={onChange}
              />

              <Label htmlFor='mobilePhone'>Mobile phone</Label>
              {isEditing ?
                <Input
                  type='phone'
                  name='mobilePhone'
                  id='mobilePhone'
                  value={values.mobilePhone}
                  onChange={onChange}
                  disabled
                /> :
                <Input
                  type='phone'
                  name='mobilePhone'
                  id='mobilePhone'
                  value={values.mobilePhone}
                  onChange={onChange}
                />
              }

              <Label htmlFor='emergencyAccess'>Emergency access</Label>
              <TextArea
                name='emergencyAccess'
                placeholder='Optional'
                id='emergencyAccess'
                value={values.emergencyAccess}
                onChange={onChange}
              />
              <Text variant='note' className={style.emergencyNote}>
                This information is only shared with your emergency
                contacts when your alarm goes off. Include key
                locations, security codes, etc., to access your home for
                the care of your pets.
              </Text>

              {customError && <ErrorText>{customError}</ErrorText>}

              {isEditing ? (
                isConfirmingRemove ? (
                  <React.Fragment>
                    <Text>Are you sure you want to remove this address?</Text>
                    <Button
                      onClick={onCancelRemove}
                      variant='hollowRed'
                      className={style.button}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={onConfirmRemove}
                      variant='red'
                      className={style.button}
                    >
                      Remove
                    </Button>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <Button type='submit' className={style.button}>
                      Save
                    </Button>
                    <Button onClick={onRemove} variant='hollowRed'>
                      Remove Address
                    </Button>
                  </React.Fragment>
                )
              ) : (
                <React.Fragment>
                  <Button type='submit' className={style.button}>
                    Save
                  </Button>
                </React.Fragment>
              )}
            </React.Fragment>
          )
        }
      </Form>
    </div>
  )
}

export default AddressForm
